/**
 * Modern Login Page – Premium SaaS-style split screen
 * Left: Brand panel | Right: Login form
 * Logic unchanged – only UI updated
 */

import { useState, useEffect, FormEvent } from 'react';
import { Eye, EyeOff, Lock, Mail, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { validateEmail, validatePassword } from '../utils/validators';
import { realAPI } from '../api/realEndpoints';
import Button from '../components/Button';
import Modal from '../components/Modal';

export default function Login() {
  const { login } = useAuth();
  const { success, error } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; general?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordErrors, setForgotPasswordErrors] = useState<{ email?: string; general?: string }>({});

  /**
   * Load saved login credentials on page load
   */
  useEffect(() => {
    const savedRemember = localStorage.getItem("rememberMe") === "true";
    const savedEmail = localStorage.getItem("savedEmail");
    const savedPassword = localStorage.getItem("savedPassword");

    if (savedRemember) {
      if (savedEmail) setEmail(savedEmail);
      if (savedPassword) setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});

    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    if (emailError) return setErrors({ email: emailError });
    if (passwordError) return setErrors({ password: passwordError });

    setIsLoading(true);

    try {
      if (rememberMe) {
        localStorage.setItem("savedEmail", email);
        localStorage.setItem("savedPassword", password);
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("savedEmail");
        localStorage.removeItem("savedPassword");
        localStorage.setItem("rememberMe", "false");
      }

      await login({ email, password });

    } catch (error) {
      setErrors({ general: "Invalid email or password" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setForgotPasswordErrors({});

    const emailError = validateEmail(forgotPasswordEmail);
    if (emailError) {
      setForgotPasswordErrors({ email: emailError });
      return;
    }

    setIsForgotPasswordLoading(true);

    try {
      await realAPI.auth.forgotPassword(forgotPasswordEmail);
      success('Reset Email Sent', 'Password reset link has been sent to your email');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
      setForgotPasswordErrors({});
    } catch (err: any) {
      if (err.status === 404) {
        setForgotPasswordErrors({ email: "This email isn't registered" });
        error('Email Not Registered', "This email isn't registered.");
      } else {
        setForgotPasswordErrors({ general: 'Unable to send reset email. Please try again later.' });
        error('Failed to Send Email', 'Unable to send reset email. Please try again later.');
      }
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  const features = [
    'Comprehensive Management Tools',
    'Real-time Analytics & Insights',
    'Secure & Reliable Platform',
  ];

  return (
    <div className="min-h-screen flex">

      {/* ── Left – Brand Panel ── */}
      <div
        className="hidden lg:flex lg:w-[46%] xl:w-[42%] relative overflow-hidden flex-col justify-between p-12"
        style={{
          background: 'linear-gradient(160deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
        }}
      >
        {/* Decorative blobs */}
        <div className="absolute top-[-60px] right-[-60px] w-72 h-72 rounded-full bg-white/5 blur-2xl pointer-events-none" />
        <div className="absolute bottom-[-80px] left-[-40px] w-80 h-80 rounded-full bg-primary-500/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 right-0 w-48 h-48 rounded-full bg-indigo-400/10 blur-2xl pointer-events-none" />

        {/* Top: Logo */}
        <div className="relative">
          <img
            src="/logo1.png"
            alt="Looks"
            className="h-12 w-auto object-contain "
            loading="eager"
          />
        </div>

        {/* Middle: Headline + Features */}
        <div className="relative space-y-8">
          <div>
            <h1 className="text-4xl font-bold text-white leading-tight mb-4">
              Manage everything<br />in one place
            </h1>
            <p className="text-indigo-200 text-base leading-relaxed max-w-sm">
              The admin dashboard for your beauty services platform.
              Monitor, manage, and grow — all from here.
            </p>
          </div>

          <div className="space-y-3">
            {features.map((f) => (
              <div key={f} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-3.5 h-3.5 text-indigo-200" />
                </div>
                <span className="text-indigo-100 text-sm font-medium">{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom: copyright */}
        <p className="relative text-indigo-300/60 text-xs">
          © {new Date().getFullYear()} Looks Admin
        </p>
      </div>

      {/* ── Right – Login Form ── */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 bg-[#f5f6fa]">
        <div className="w-full max-w-md animate-fade-in">

          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo1.png" alt="Looks" className="h-10 w-auto object-contain" />
          </div>

          {/* Form card */}
          <div className="bg-white rounded-2xl shadow-float border border-surface-border p-8">

            <div className="mb-7">
              <h2 className="text-2xl font-bold text-ink mb-1.5">Welcome back</h2>
              <p className="text-sm text-ink-muted">Sign in to access your admin dashboard</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-ink-muted mb-1.5">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-ink-subtle" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                    autoComplete="email"
                    required
                    className={`input-base pl-10 ${errors.email ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''}`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0 inline-block" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-ink-muted mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 text-ink-subtle" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                    className={`input-base pl-10 pr-11 ${errors.password ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-ink-subtle hover:text-ink transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0 inline-block" />
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Remember / Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-surface-border-strong text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-ink-muted">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  Forgot password?
                </button>
              </div>

              {/* General error */}
              {errors.general && (
                <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0 inline-block" />
                    {errors.general}
                  </p>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                loading={isLoading}
                size="lg"
                className="w-full font-semibold"
              >
                {isLoading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>

            <p className="mt-7 text-center text-xs text-ink-subtle">
              © {new Date().getFullYear()} Looks Admin. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={showForgotPassword}
        onClose={() => {
          setShowForgotPassword(false);
          setForgotPasswordEmail('');
          setForgotPasswordErrors({});
        }}
        title="Reset password"
        description="Enter your email address and we'll send you a reset link."
      >
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div>
            <label htmlFor="forgot-email" className="block text-sm font-medium text-ink-muted mb-1.5">
              Email address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <Mail className="h-4 w-4 text-ink-subtle" />
              </div>
              <input
                id="forgot-email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className={`input-base pl-10 ${forgotPasswordErrors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
              />
            </div>
            {forgotPasswordErrors.email && (
              <p className="mt-1.5 text-xs text-red-600">{forgotPasswordErrors.email}</p>
            )}
          </div>

          {forgotPasswordErrors.general && (
            <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-700">{forgotPasswordErrors.general}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-1">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowForgotPassword(false);
                setForgotPasswordEmail('');
                setForgotPasswordErrors({});
              }}
              disabled={isForgotPasswordLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isForgotPasswordLoading}
              disabled={!forgotPasswordEmail}
            >
              {isForgotPasswordLoading ? 'Sending…' : 'Send reset link'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
